import { buildLoginUrl, readApp, readProgramId } from '../shared/utils/appUrl';
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { apiCall } from '../utils/api';
import { AdminAppState, ProgramStats, SystemConfig } from '../shared/types/system';
import { useSession } from './useSession';
import { useRegistryHydration } from './useRegistryHydration';
import { useRegistryPatch } from './useRegistryPatch';
import { useAsyncEffect } from './useAsyncEffect';

interface AdminContextType {
  state: AdminAppState | null;
  loading: boolean;
  isRefreshing: boolean;
  isReadOnly: boolean;
  viewingCampaignId: string | null;
  isSessionNearExpiry: boolean;
  lastSyncedAt: Date | null;
  error: string | null;
  isLiveMode: boolean;
  setLiveMode: (isLive: boolean) => void;
  refresh: () => Promise<void>;
  forceGlobalRefresh: () => Promise<void>;
  renew: () => Promise<void>;
  hydrateRegistry: (name: keyof AdminAppState, force?: boolean) => Promise<unknown[] | undefined>;
  invalidateRegistries: (names: (keyof AdminAppState)[]) => void;
  patchState: (type: keyof AdminAppState, updatedRecord: any, idField: string) => void;
  patchConfig: (updates: Partial<SystemConfig>) => void;
  setState: (state: AdminAppState) => void;
}

const AdminContext = createContext<AdminContextType | null>(null);

// Mirrors CAMPAIGN_SCOPED_REGISTRIES (src/server/registries.ts) — keep in sync manually. Used to
// reset exactly the campaign-scoped slices of AdminAppState when the viewed campaign changes
// (ADR-074 Stage 2), so a switch never leaves stale data from the previous context behind.
const CAMPAIGN_SCOPED_STATE_KEYS: ReadonlySet<keyof AdminAppState> = new Set([
  'households', 'members', 'gifts', 'tags', 'bags', 'inventory', 'applications', 'events', 'slots', 'transactions'
]);

/**
 * Provider to manage contextual hydration state for the Admin Portal.
 *
 * `opsMode` (ADR-109): set by the Ops Hub's `AdminStateProvider` mounts (`App.tsx`'s
 * `ops` case) so bulk hydration routes to the unconditionally PII-redacted
 * `getOps*` endpoints regardless of the session's actual role — PII visibility here is
 * decided by which frontend context mounted the provider, not by session role.
 */
export function AdminStateProvider({ children, opsMode = false }: { children: React.ReactNode; opsMode?: boolean }) {
  const [state, setState] = useState<AdminAppState | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);
  const [isAuthenticating, setIsAuthenticating] = useState<boolean>(false);
  const [isReadOnly, setIsReadOnly] = useState<boolean>(false);
  const [lastSyncedAt, setLastSyncedAt] = useState<Date | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLiveMode, setLiveMode] = useState<boolean>(false);

  const lastActivityRef = React.useRef<number>(0);
  const lastRefreshRef = React.useRef<number>(0);

  // ADR-074 Stage 2: historical campaign browsing, driven entirely by the ?campaign= URL param.
  const [searchParams] = useSearchParams();
  const campaignParam = searchParams.get('campaign');
  // undefined = "haven't compared yet" (first run) — distinct from null, which is a legitimate
  // "live mode" value. Lets refresh() tell a genuine context switch apart from a live->live no-op.
  const prevCampaignRef = React.useRef<string | null | undefined>(undefined);

  useEffect(() => {
    lastActivityRef.current = Date.now();
    const updateActivity = () => { lastActivityRef.current = Date.now(); };
    window.addEventListener('mousemove', updateActivity);
    window.addEventListener('keydown', updateActivity);
    window.addEventListener('click', updateActivity);
    window.addEventListener('scroll', updateActivity);
    return () => {
      window.removeEventListener('mousemove', updateActivity);
      window.removeEventListener('keydown', updateActivity);
      window.removeEventListener('click', updateActivity);
      window.removeEventListener('scroll', updateActivity);
    };
  }, []);

  const { isSessionNearExpiry, renew } = useSession(setState, state?.session, lastActivityRef);
  const { hydrateRegistry } = useRegistryHydration(state, setState, campaignParam, opsMode);
  const { patchState, patchConfig } = useRegistryPatch(setState);

  // ADR-096: fire-and-forget re-fetch of registries a mutation is known to have touched
  // server-side beyond the one it already `patchState`s directly (e.g. promoting an application
  // also creates/updates Household and Member rows this client has no way to hand-patch).
  const invalidateRegistries = useCallback((names: (keyof AdminAppState)[]) => {
    names.forEach(name => { hydrateRegistry(name, true); });
  }, [hydrateRegistry]);

  const refresh = useCallback(async () => {
    await Promise.resolve();
    setIsRefreshing(true);
    setError(null);
    try {
      // Campaigns Room "Open" link (DL-261): forces a real reauth on this one load rather than
      // trusting the browser's own same-origin sessionStorage-cloning behavior for a new tab,
      // which empirically still clones the token into an auxiliary browsing context even with
      // rel="noopener" set on the anchor — noopener only severs the window.opener JS reference,
      // it doesn't change storage semantics. The flag never reappears in the URL once the OAuth
      // round trip completes (LoginPage's post-auth redirect doesn't carry it), so this only
      // bypasses the token check on this single initial load, not on every future refresh.
      const forceReauth = new URLSearchParams(window.location.search).get('forceReauth') === '1';
      // ADR-129: Admin Portal and Ops Hub sessions are separate — each reads only its own token.
      const isOpsApp = readApp(new URLSearchParams(window.location.search)) === 'ops';
      let token = forceReauth ? null : (isOpsApp
                    ? (localStorage.getItem('volunteer_session_token') || sessionStorage.getItem('volunteer_session_token'))
                    : sessionStorage.getItem('session_token'));

      // DL-515: local review environment only (`npm run dev:review` sets the flag; `import.meta.env.DEV`
      // makes Vite drop this block from every production/staging build). With no session, mint a dev
      // admin one through the same server-gated action the seeder uses — the server still refuses it
      // outside non-production + the designated fixture database + the correct access code.
      // Sign Out sets `review_autologin_off` (sessionStorage, DL-545: scoped to that tab, so a new tab or a Quick Links
      // click signs in again) so the real login screens stay reachable for testing other roles and OTP;
      // `&autologin=1` clears it (dev admin again), `&autologin=0` sets it. The param is stripped once read.
      let autologinOff = false;
      if (import.meta.env.DEV && import.meta.env.VITE_REVIEW_AUTOLOGIN === '1') {
        try {
          const url = new URL(window.location.href);
          const override = url.searchParams.get('autologin');
          if (override === '1') sessionStorage.removeItem('review_autologin_off');
          else if (override === '0') sessionStorage.setItem('review_autologin_off', '1');
          if (override !== null) {
            url.searchParams.delete('autologin');
            window.history.replaceState(null, '', url.toString());
          }
          autologinOff = sessionStorage.getItem('review_autologin_off') === '1';
        } catch { /* storage unavailable: autologin stays on */ }
      }
      if (!token && !forceReauth && !autologinOff && import.meta.env.DEV && import.meta.env.VITE_REVIEW_AUTOLOGIN === '1') {
        try {
          token = await apiCall<string>('dev_mintAdminSession', import.meta.env.VITE_REVIEW_ACCESS_CODE || '');
          // ADR-129: store under the key this app reads (the Ops Hub no longer falls back to `session_token`).
          if (isOpsApp) {
            localStorage.setItem('volunteer_session_token', token);
            // App.tsx reads the Ops token once per render to choose Hub vs. login gateway — reload so it sees it.
            window.location.reload();
            return;
          }
          sessionStorage.setItem('session_token', token);
        } catch (err) {
          console.warn('[review autologin] could not mint a dev admin session; falling back to SSO.', err);
        }
      }

      if (!token) {
        const urlParams = new URLSearchParams(window.location.search);
        const dbId = readProgramId(urlParams) || window.GRM_CONFIG?.dbId;
        const app = readApp(urlParams) ?? 'admin';

        if (app === 'admin') {
          setIsAuthenticating(true);
          window.location.href = buildLoginUrl('', { app, programId: dbId }, { campaign: campaignParam });
          return;
        }

        setLoading(false);
        setIsRefreshing(false);
        return;
      }

      // DL-563: the Ops Hub loads a lighter snapshot (no stats / sender account / admin-only config keys).
      const data = await apiCall<any>(opsMode ? 'getOpsSnapshot' : 'getProgramSnapshot', campaignParam || undefined);
      if (!data?.manifest) {
        throw new Error('Program Snapshot is missing the Master Manifest.');
      }

      // A genuine change in which campaign is being viewed (not the initial mount, and not a
      // live->live or historical-A->historical-A no-op refresh) — the previously-hydrated
      // campaign-scoped registries belong to the OLD context and must not leak into the new one.
      const contextSwitched = prevCampaignRef.current !== undefined && prevCampaignRef.current !== campaignParam;
      prevCampaignRef.current = campaignParam;

      setState(current => {
        const manifest = data.manifest;
        if (data.config && (data.config.IS_READ_ONLY === true || String(data.config.IS_READ_ONLY).toLowerCase() === 'true')) {
          setIsReadOnly(true);
        } else {
          setIsReadOnly(false);
        }

        const preserveScoped = <T,>(key: keyof AdminAppState, fallback: T[]): T[] =>
          (contextSwitched ? fallback : (current?.[key] as T[] | undefined)) || fallback;

        const baseState: AdminAppState = {
          config: data.config,
          manifest: manifest,
          session: data.session,
          metrics: data.metrics ?? {},
          households: preserveScoped('households', []),
          members: preserveScoped('members', []),
          gifts: preserveScoped('gifts', []),
          tags: preserveScoped('tags', []),
          bags: preserveScoped('bags', []),
          venues: current?.venues || [],
          categories: manifest?.categories || current?.categories || [],
          applicationFields: manifest?.forms?.application || current?.applicationFields || [],
          policies: manifest?.policies || [],
          inventory: preserveScoped('inventory', []),
          fulfillmentGroupingRules: manifest?.fulfillmentGroupingRules || current?.fulfillmentGroupingRules || [],
          applications: preserveScoped('applications', []),
          events: preserveScoped('events', []),
          slots: preserveScoped('slots', []),
          donors: current?.donors || [],
          transactions: preserveScoped('transactions', []),
          users: current?.users || [],
          campaigns: current?.campaigns || [],
          stats: (data.stats || current?.stats || data.metrics || {}) as ProgramStats,
          statsUnavailable: data.statsUnavailable === true,
          serverTimestamp: data.serverTimestamp,
          senderStatus: data.senderStatus || current?.senderStatus,
          hydrated: {
            ...(contextSwitched
              ? Object.fromEntries(Object.entries(current?.hydrated || {}).filter(([k]) => !CAMPAIGN_SCOPED_STATE_KEYS.has(k as keyof AdminAppState)))
              : current?.hydrated),
            categories: !!manifest?.categories,
            fulfillmentGroupingRules: !!manifest?.fulfillmentGroupingRules,
            applicationFields: !!manifest?.forms?.application
          }
        };
        return baseState;
      });
      setLastSyncedAt(new Date());
    } catch (err: any) {
      console.error('Failed to fetch program snapshot:', err);
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  }, [campaignParam, opsMode]);

  // DL-309: "Force Refresh Data" needs to reach every Room, not just whichever one is currently
  // mounted (a plain refresh() preserves campaign-scoped registries as-is — see preserveScoped
  // above — so a not-visited Room silently kept stale data until manually revisited). Every Room
  // already has its own "if this registry isn't hydrated yet, fetch it" mount effect (Contextual
  // Hydration), so marking every currently-hydrated registry stale here — without touching the
  // data itself — makes that effect re-fire on its own: immediately for the mounted Room, or the
  // next time an unvisited Room mounts. No per-Room refresh wiring needed.
  const forceGlobalRefresh = useCallback(async () => {
    await refresh();
    setState(current => {
      if (!current) return current;
      const staleHydrated = Object.fromEntries(
        Object.entries(current.hydrated).map(([key, isHydrated]) => [key, isHydrated ? false : isHydrated])
      ) as typeof current.hydrated;
      return { ...current, hydrated: staleHydrated };
    });
  }, [refresh]);

  // Auto-refresh every 5 minutes, but ONLY if in Live Mode (Stats Dashboard). Also fires
  // immediately on tab-return (visibilitychange) rather than waiting for the next interval
  // tick, so stepping away and back doesn't leave the dashboard stale for up to 5 minutes —
  // debounced against the interval's own recent fire so rapid tab-switching doesn't spam calls.
  useEffect(() => {
    if (!isLiveMode) return;

    const maybeRefresh = () => {
      const isVisible = document.visibilityState === 'visible';
      const isIdle = Date.now() - lastActivityRef.current > 15 * 60 * 1000;
      const debounced = Date.now() - lastRefreshRef.current < 30 * 1000;

      if (isVisible && !isIdle && !debounced) {
        lastRefreshRef.current = Date.now();
        refresh();
      }
    };

    const timer = setInterval(maybeRefresh, 5 * 60 * 1000);
    document.addEventListener('visibilitychange', maybeRefresh);

    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', maybeRefresh);
    };
  }, [refresh, isLiveMode]);

  useAsyncEffect(() => {
    refresh();
  }, [refresh]);

  return (
    <AdminContext.Provider value={{
      state,
      loading: loading || isAuthenticating,
      isRefreshing,
      isReadOnly,
      viewingCampaignId: campaignParam,
      isSessionNearExpiry,
      lastSyncedAt,
      error,
      isLiveMode,
      setLiveMode,
      refresh,
      forceGlobalRefresh,
      renew,
      hydrateRegistry,
      invalidateRegistries,
      patchState,
      patchConfig,
      setState
    }}>
      {children}
    </AdminContext.Provider>
  );
}

/**
 * Hook to access the shared Admin state.
 * Does NOT trigger a fetch on mount.
 */
export function useAdminState() {
  const context = useContext(AdminContext);
  if (!context) {
    throw new Error('useAdminState must be used within an AdminStateProvider');
  }
  return context;
}
